import React, { useEffect, useState } from "react";
import axios from "axios";
import { SERVER_URL } from "../config";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import "../styles/questionnaireEdit.css";

interface Condition {
  field?: string;
  operator?: string;
  value?: string;
}

interface AnswerOption {
  code: string;
  key: string;
  text: string;
  next?: string;
}

interface Field {
  key: string;
  label: string;
  inputType: "text" | "date" | "phone";
}

interface Question {
  id: string;
  type: string;
  header: string;
  description?: string;
  condition?: Condition;
  next?: string;
  answerOptions: AnswerOption[];
  fields?: Field[];
}

interface Questionnaire {
  id: string;
  title: string;
  description?: string;
  content?: { questions: Question[]; [k: string]: any };
  createdAt: string;
  updatedAt: string;
}

const QuestionnaireEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(
    null
  );
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // fetch
  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${SERVER_URL}/api/questionnaire/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data: Questionnaire = res.data;
        setQuestionnaire(data);
        setTitle(data.title);
        setDesc(data.description || "");
        setQuestions(data.content?.questions || []);
      } catch (e) {
        console.error(e);
        setError("Failed to load questionnaire.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // ---------- helpers ----------
  const updateQuestion = (i: number, upd: Partial<Question>) =>
    setQuestions((q) => {
      const qs = [...q];
      qs[i] = { ...qs[i], ...upd };
      return qs;
    });

  const addAnswerOption = (qIdx: number) =>
    setQuestions((q) => {
      const qs = [...q];
      qs[qIdx].answerOptions.push({
        code:
          qs[qIdx].id.toUpperCase() +
          String.fromCharCode(65 + qs[qIdx].answerOptions.length),
        key: "",
        text: "",
      });
      return qs;
    });

  // blank template
  const makeBlankQuestion = (): Question => ({
    id: ``,
    type: "text",
    header: "",
    description: "",
    answerOptions: [],
    fields: [],
  });

  const addQuestion = (idx: number) =>
    setQuestions((prev) => {
      const qs = [...prev];
      qs.splice(idx, 0, makeBlankQuestion()); // drops a blank question at idx
      return qs;
    });

  // delete with confirm
  const removeQuestion = (idx: number) => {
    if (!window.confirm("Are you sure you want to delete this question?"))
      return;
    setQuestions((q) => q.filter((_, i) => i !== idx));
  };

  const updateAnswerOption = (
    qIdx: number,
    oIdx: number,
    upd: Partial<AnswerOption>
  ) =>
    setQuestions((q) => {
      const qs = [...q];
      const opts = [...qs[qIdx].answerOptions];
      if (upd.code) upd.code = upd.code.toUpperCase();
      opts[oIdx] = { ...opts[oIdx], ...upd };
      qs[qIdx].answerOptions = opts;
      return qs;
    });

  const removeAnswerOption = (qIdx: number, oIdx: number) =>
    setQuestions((q) => {
      const qs = [...q];
      qs[qIdx].answerOptions = qs[qIdx].answerOptions.filter(
        (_, i) => i !== oIdx
      );
      return qs;
    });

  // ----- new field helpers -----
  const addField = (qIdx: number) =>
    setQuestions((q) => {
      const qs = [...q];
      qs[qIdx].fields = qs[qIdx].fields || [];
      qs[qIdx].fields!.push({ key: "", label: "", inputType: "text" });
      return qs;
    });

  const updateField = (qIdx: number, fIdx: number, upd: Partial<Field>) =>
    setQuestions((q) => {
      const qs = [...q];
      const fs = qs[qIdx].fields || [];
      fs[fIdx] = { ...fs[fIdx], ...upd };
      qs[qIdx].fields = fs;
      return qs;
    });

  const removeField = (qIdx: number, fIdx: number) =>
    setQuestions((q) => {
      const qs = [...q];
      qs[qIdx].fields = (qs[qIdx].fields || []).filter((_, i) => i !== fIdx);
      return qs;
    });

  // save
  const handleSave = async () => {
    if (!questionnaire) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${SERVER_URL}/api/questionnaire/${questionnaire.id}`,
        {
          title,
          description: desc,
          content: { ...(questionnaire.content || {}), questions },
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Updated!");
    } catch (e) {
      console.error(e);
      toast.error("Save failed.");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !questionnaire) return <p>Loading…</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!questionnaire) return <p>Not found.</p>;

  return (
    <div className="edit-questionnaire-container">
      <h2>Edit Questionnaire</h2>

      {/* title/desc */}
      <div className="edit-field-group">
        <label>Title</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className="edit-field-group">
        <label>Description</label>
        <textarea value={desc} onChange={(e) => setDesc(e.target.value)} />
      </div>

      <hr />

      {/* questions */}
      <h3>Questions</h3>
      {questions.map((q, qIdx) => (
        <div key={qIdx} className="question-card">
          <button type="button" onClick={() => removeQuestion(qIdx)}>
            🗑 Remove
          </button>

          {/* id */}
          <div className="edit-field-group">
            <label>Code</label>
            <input
              value={q.id}
              onChange={(e) =>
                updateQuestion(qIdx, { id: e.target.value.toUpperCase() })
              }
            />
          </div>

          {/* header */}
          <div className="edit-field-group">
            <label>Header</label>
            <input
              value={q.header}
              onChange={(e) => updateQuestion(qIdx, { header: e.target.value })}
            />
          </div>

          {/* type */}
          <div className="edit-field-group">
            <label>Type</label>
            <select
              value={q.type}
              onChange={(e) => updateQuestion(qIdx, { type: e.target.value })}
            >
              <option value="text">Text</option>
              <option value="choice">Choice</option>
              <option value="multiple-choice">Multiple Choice</option>
              <option value="height-weight">Height-Weight</option>
              <option value="number">Number</option>
              <option value="disqualify">Disqualify</option>
            </select>
          </div>

          {/* description */}
          <div className="edit-field-group">
            <label>Description</label>
            <textarea
              value={q.description || ""}
              onChange={(e) =>
                updateQuestion(qIdx, { description: e.target.value })
              }
            />
          </div>

          {/* fields (new) */}
          {q.type === "text" && (
            <div className="edit-field-group">
              <label>Fields</label>
              {(q.fields || []).map((f, fIdx) => (
                <div key={fIdx} className="field-row">
                  <select
                    value={f.inputType}
                    onChange={(e) =>
                      updateField(qIdx, fIdx, {
                        inputType: e.target.value as any,
                      })
                    }
                  >
                    <option value="text">Text</option>
                    <option value="date">Date</option>
                    <option value="phone">Phone</option>
                  </select>
                  <input
                    placeholder="key"
                    value={f.key}
                    onChange={(e) =>
                      updateField(qIdx, fIdx, { key: e.target.value })
                    }
                  />
                  <input
                    placeholder="label"
                    value={f.label}
                    onChange={(e) =>
                      updateField(qIdx, fIdx, { label: e.target.value })
                    }
                  />
                  <button type="button" onClick={() => removeField(qIdx, fIdx)}>
                    ×
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => addField(qIdx)}>
                Add Field
              </button>
            </div>
          )}

          {/* answer options */}
          {(q.type === "choice" || q.type === "multiple-choice") && (
            <div className="answer-options-section">
              <h4>Answer Options</h4>
              {q.answerOptions.map((opt, oIdx) => (
                <div key={oIdx} className="answer-option-row">
                  <input
                    placeholder="Code"
                    value={opt.code}
                    onChange={(e) =>
                      updateAnswerOption(qIdx, oIdx, { code: e.target.value })
                    }
                  />
                  <input
                    placeholder="Key"
                    value={opt.key}
                    onChange={(e) =>
                      updateAnswerOption(qIdx, oIdx, { key: e.target.value })
                    }
                  />
                  <input
                    placeholder="Text"
                    value={opt.text}
                    onChange={(e) =>
                      updateAnswerOption(qIdx, oIdx, { text: e.target.value })
                    }
                  />
                  <input
                    placeholder="Next"
                    value={opt.next || ""}
                    onChange={(e) =>
                      updateAnswerOption(qIdx, oIdx, { next: e.target.value })
                    }
                  />
                  <button
                    type="button"
                    onClick={() => removeAnswerOption(qIdx, oIdx)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => addAnswerOption(qIdx)}>
                Add Answer Option
              </button>
            </div>
          )}
          <button type="button" onClick={() => addQuestion(qIdx + 1)}>
            + Add Question
          </button>
        </div>
      ))}

      {/* save */}
      <button
        disabled={loading}
        onClick={handleSave}
        className="save-changes-button"
      >
        {loading ? "Saving…" : "Save Changes"}
      </button>
    </div>
  );
};

export default QuestionnaireEdit;
